import type { PayloadAction } from "@reduxjs/toolkit"
import { createSlice } from "@reduxjs/toolkit"
import { removeRefreshToken, setRefreshToken } from "./refreshTokenStorage"
import jwtDecode from "./jwtDecode"
import type { TokenResponse } from "./model"
import { clearAbly } from "@/base/ably"
import { trackUser } from "@/monitoring"
import type { Uuid } from "@/utils"

export type State = {
  isLoading: boolean
  userId: Uuid | null
  roles: string[]
  token: string | null
  tenantSlug: string | null
}

const initialState: State = {
  isLoading: true,
  userId: null,
  roles: [],
  token: null,
  tenantSlug: null,
}

const slice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    updateTokens(
      state,
      { payload: { token, refreshToken } }: PayloadAction<TokenResponse>,
    ) {
      const { userId, roles } = jwtDecode(token)

      // A refresh can return another tab's newer sign-in, as someone else
      // whose workspaces this one may not belong to
      if (userId !== state.userId) {
        state.tenantSlug = null
      }
      state.userId = userId
      state.roles = roles
      state.token = token
      state.isLoading = false

      setRefreshToken(refreshToken)

      void trackUser(userId)
    },
    clearAuth(state) {
      state.userId = null
      state.roles = []
      state.token = null
      state.tenantSlug = null
      state.isLoading = false

      removeRefreshToken()

      clearAbly()
    },
    setTenantSlug(state, action: PayloadAction<string>) {
      state.tenantSlug = action.payload
    },
  },
  selectors: {
    getToken: (state) => state.token,
    getTokenAvailable: (state) => !!state.token,
    getLoading: (state) => state.isLoading,
    getUserId: (state) => state.userId,
    getRoles: (state) => state.roles,
    getTenantSlug: (state) => state.tenantSlug,
  },
})

export const { updateTokens, clearAuth, setTenantSlug } = slice.actions
export const {
  getToken,
  getTokenAvailable,
  getLoading,
  getUserId,
  getRoles,
  getTenantSlug,
} = slice.selectors

export default slice
